import React, { useCallback, useContext, useMemo } from 'react';
import styles from './CreateTiktokInfluencerPostModal.module.css';
import { GenericDropdownOption } from '../../../../../../App.types';
import Modal, { ModalContent, ModalTitle } from '../../../../../../ui/General/Modal/Modal';
import TiktokInfluencerPostForm, {
    TiktokInfluencerPostFormValues,
} from '../InfluencerPostForm/TiktokInfluencerPostForm';
import useNonNullContext from '../../../../../../Hooks/useNonNullContext';
import {
    TiktokCreatorsContext,
    TiktokCreatorsStateContext,
} from '../../../contexts/TiktokCreatorsContext/TiktokCreatorsContext';
import { FormikHelpers } from 'formik';
import { InfluencerPlanContext } from '../../../contexts/InfluencerPlanContext';
import { OptionsContext } from '../../../../../../contexts/OptionsContext/OptionsContext';
import { getCostErrorFields, getCostFields } from '../../../InfluencerPlan.helpers';
import { mapApiErrorsToFormikErrors } from '../../../../../../utility/utility';
import { showNotification } from '../../../../../../helpers';

type CreateTiktokInfluencerPostModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    groupId: number | null;
};

const CreateTiktokInfluencerPostModal = ({
    isOpen,
    onClose,
    onCreated,
    groupId,
}: CreateTiktokInfluencerPostModalProps) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const { createTiktokInfluencerPost } = useNonNullContext(TiktokCreatorsContext);
    const { initialized, influencerPlan } = useContext(InfluencerPlanContext);
    const { tiktokInfluencerPostGroups } = useNonNullContext(TiktokCreatorsStateContext);

    const currencyOptions: GenericDropdownOption<number>[] = useMemo(
        () =>
            currencies.map((c) => ({
                value: c.id,
                label: c.name,
            })),
        [currencies]
    );

    const initialValues: TiktokInfluencerPostFormValues = {
        influencer_id: null,
        currency_id: currencyOptions[0],
        cost: '',
        xero_invoice_id: null,
        is_carousel: false,
        post_url: '',
    };

    const handleSubmit = useCallback(
        async (
            values: TiktokInfluencerPostFormValues,
            { setSubmitting, setErrors }: FormikHelpers<TiktokInfluencerPostFormValues>
        ) => {
            if (!initialized || !influencerPlan || !groupId) {
                return;
            }

            try {
                setSubmitting(true);
                const group = tiktokInfluencerPostGroups.find((g) => g.id === groupId);
                const shouldUseBaseCost = group?.is_shared_with_creatorbase === true;

                const basePostData = {
                    influencer_id: values.influencer_id?.value ?? null,
                    currency_id: values.currency_id?.value ?? currencyOptions[0].value,
                    post_url: values.post_url ?? '',
                    xero_invoice_id: values.xero_invoice_id ? values.xero_invoice_id.value : null,
                    is_carousel: values.is_carousel ?? false,
                };

                const response = await createTiktokInfluencerPost(groupId, {
                    ...basePostData,
                    ...getCostFields(values.cost ?? '', shouldUseBaseCost),
                });

                if (!response) {
                    showNotification("Could't create post. No associated influencer plan", 'error');
                    return;
                }

                if (response.status === 400) {
                    setErrors(
                        mapApiErrorsToFormikErrors({
                            ...response.data,
                            ...getCostErrorFields(response.data),
                        })
                    );
                    return;
                }
                onCreated();
            } catch {
                // no op
            } finally {
                setSubmitting(false);
            }
        },
        [
            createTiktokInfluencerPost,
            initialized,
            influencerPlan,
            groupId,
            currencyOptions,
            onCreated,
            tiktokInfluencerPostGroups,
        ]
    );

    return (
        <Modal modalClassName={styles.modal} isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
            <ModalTitle>Create Post</ModalTitle>
            <ModalContent className={styles.content}>
                <TiktokInfluencerPostForm handleSubmit={handleSubmit} initialValues={initialValues} />
            </ModalContent>
        </Modal>
    );
};

export default CreateTiktokInfluencerPostModal;
